import { Droppable } from '@hello-pangea/dnd'
import { Box, Flex, Text } from '@chakra-ui/react'
import { TreeViewDraggable } from './TreeViewDraggable'
import React, { useContext } from 'react'
import { ButtonCreateNode } from './ButtonCreateNode'
import { TreeViewContext } from './TreeViewContext'
import { ArrowDownIcon } from '@chakra-ui/icons'

import PropTypes from 'prop-types'
import {
  IReportBlueprint,
  IReportComponentBlueprint,
} from '../../../../services/decoders/blueprintDecoder'

interface TreeViewDroppableProps {
  sectionName?: string
  type: string
  parentId?: string
  droppableId: string
  disableClick?: boolean
  renderClone?: any
  data:
    | IReportComponentBlueprint
    | IReportBlueprint
    | IReportComponentBlueprint[]
  disableAccordion?: boolean
  isDropDisabled?: boolean
}
export const TreeViewDroppable = ({
  sectionName,
  type,
  parentId,
  droppableId,
  disableClick,
  renderClone,
  data,
  disableAccordion = false,
  isDropDisabled = false,
}: TreeViewDroppableProps) => {
  const { onCreateNode, readOnly } = useContext(TreeViewContext)
  const canCreateNode =
    ['section', 'subsection'].includes(type) && // only allow subsections to be added to sections
    !disableClick && // disable adding nodes when clicking on a node
    !readOnly && // disable adding nodes when in read only mode
    ((data as IReportComponentBlueprint)?.children || data)?.every(item =>
      ['subsection', 'section'].includes(item.type),
    ) // avoid to create new subsections if the section already has charts

  const canDrop =
    !readOnly && // disable dropping when in read only mode
    !disableClick && // disable dropping when clicking on a node
    !['MAIN', 'section'].includes(type) && // disable dropping when the node is a section or the root
    !(data as IReportComponentBlueprint)?.children?.some(
      item => item.type === 'subsection',
    ) // disable dropping when there is already a subsection

  return (
    <Droppable
      isDropDisabled={isDropDisabled}
      droppableId={droppableId}
      type={type}
      renderClone={renderClone}>
      {(provided, snapshot) => (
        <Box
          {...provided.droppableProps}
          ref={provided.innerRef}
          position="relative"
          border="1px dashed"
          borderColor={snapshot.isDraggingOver ? 'brand.gray' : 'transparent'}
          background={snapshot.isDraggingOver ? 'brand.lightestGray' : 'white'}>
          {!data && !disableClick ? (
            <>
              <EmptyArea id={droppableId} />
              {provided.placeholder}
            </>
          ) : (
            <>
              <TreeViewDraggable
                data={data}
                disableClick={disableClick}
                disableAccordion={disableAccordion}
              />
              {canDrop && <EmptyArea id={droppableId} />}
              {provided.placeholder}
              {canCreateNode && (
                <Box ml="60px">
                  <ButtonCreateNode
                    target={data as IReportComponentBlueprint[]}
                    label={
                      <Text>
                        Add Subsection at <strong>{sectionName}</strong>
                      </Text>
                    }
                    parentId={parentId}
                    type="subsection"
                    onSubmit={onCreateNode}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Droppable>
  )
}

TreeViewDroppable.propTypes = {
  data: PropTypes.array,
}

interface EmptyAreaProps {
  id: string
}
const EmptyArea = ({ id }: EmptyAreaProps) => {
  const { selectedNodeId } = useContext(TreeViewContext)
  if (selectedNodeId !== id) return null
  return (
    <Flex
      width="100%"
      ml="28px"
      alignItems="center"
      onMouseOver={e => e.stopPropagation()}
      onDragOver={e => e.stopPropagation()}>
      <ArrowDownIcon
        fontSize="14px"
        color="brand.gray"
      />
      <Text
        fontSize="14px"
        p="8px 12px"
        color="brand.darkGray">
        Drop items here
      </Text>
    </Flex>
  )
}
